// plugins/inactivity-tracker.client.js
import { DateTime } from 'luxon';
import { throttle } from 'lodash';
import { removeJwt } from '@/utils/jwt';

export default ({ app, store, $auth, $cookies, $braze }) => {
  if (!process.client) return;

  const INACTIVITY_LIMIT_MINUTES = 20;
  const CHECK_INTERVAL_MS = 60 * 1000;
  const ACTIVITY_EVENTS = ['click', 'touchstart', 'mousemove', 'mousedown', 'scroll', 'keydown'];

  // ---- Save last activity ----
  const saveActivity = () => {
    localStorage.setItem('lastActivity', DateTime.now().toISO());
  };

  // ---- Hybrid handler (idleCallback → fallback to throttle) ----
  let idleCallbackId = null;
  const activityHandler =
    typeof window.requestIdleCallback === 'function'
      ? () => {
        if (idleCallbackId) cancelIdleCallback(idleCallbackId);
        idleCallbackId = requestIdleCallback(saveActivity, { timeout: 2000 });
      }
      : throttle(saveActivity, 10000, { leading: true });

  // ---- Register event listeners ----
  ACTIVITY_EVENTS.forEach((event) =>
    window.addEventListener(event, activityHandler, { passive: true })
  );

  // ---- Initialize lastActivity ----
  saveActivity();

  // ---- Periodically check for inactivity ----
  const checkUserInactivity = async () => {
    const user = store.state.auth?.user;
    if (!user) return;

    const lastActivityISO = localStorage.getItem('lastActivity');
    if (!lastActivityISO) return;

    const lastActivity = DateTime.fromISO(lastActivityISO);
    if (!lastActivity.isValid) return;

    const minutesElapsed = DateTime.now().diff(lastActivity, 'minutes').minutes;

    if (minutesElapsed > INACTIVITY_LIMIT_MINUTES) {
      console.log('⏳ Logging out inactive user...');

      try {
        await $auth().post('/api/common/logout', { session_id: store.state.auth.sessionToken });
      } catch (e) {
        console.warn('Logout API failed:', e);
      }

      try {
        window.zE?.('messenger', 'logoutUser');
        $cookies.remove('cross-token');
        if ($braze) {
          $braze.changeUser('anonymous');
        }
      } catch (e) {
        console.warn('Cleanup integrations failed:', e);
      }

      store.commit('auth/clearAuthentication');
      removeJwt(app);
      window._alert(app.i18n.t("You've been logged out for inactivity"));
    }
  };

  const inactivityCheckInterval = setInterval(checkUserInactivity, CHECK_INTERVAL_MS);

  // ---- Cleanup when Nuxt reloads or navigates away ----
  window.addEventListener('beforeunload', () => {
    ACTIVITY_EVENTS.forEach((event) =>
      window.removeEventListener(event, activityHandler)
    );
    clearInterval(inactivityCheckInterval);
    if (idleCallbackId) cancelIdleCallback(idleCallbackId);
  });
};
